import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Droplets, ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const from = location.state?.from?.pathname || "/admin";

  async function onSubmit(e) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    try {
      await login(password);
      toast.success("Signed in");
      nav(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(typeof detail === "string" ? detail : "Too many attempts");
      } else {
        toast.error(typeof detail === "string" ? detail : "Incorrect password");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="h-14 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto h-full flex items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 text-[#334E68]">
            <Droplets className="w-5 h-5 text-[#16A34A]" strokeWidth={2.5} />
            <span className="font-display text-base font-bold tracking-tight">
              Green Services
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#334E68] flex items-center justify-center">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h1
            data-testid="admin-login-headline"
            className="mt-6 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#334E68] text-center"
          >
            Admin sign-in
          </h1>
          <p className="mt-2 text-sm text-gray-600 text-center">
            Charlie only. One password.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label
                htmlFor="password"
                className="text-sm font-medium text-[#334E68]"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-login-password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 min-h-[52px] rounded-xl border-gray-200 focus:border-[#16A34A] focus:ring-1 focus:ring-[#16A34A]"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy || !password}
              className="w-full min-h-[56px] bg-[#16A34A] hover:bg-[#15803D] disabled:bg-gray-300 text-white rounded-2xl font-semibold text-base tracking-wide uppercase flex items-center justify-center gap-2"
            >
              {busy ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign in <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
